import { useCallback, useEffect, useMemo, useState } from 'react';
import { TrendingUp, AlertCircle, Info } from 'lucide-react';
import { cashCockpitApi, plannedEventsApi } from '../../services/cashCockpitApi';
import type { ForecastData } from '../../types/cashCockpit';
import WaterfallChart from '../cash-cockpit/charts/WaterfallChart';
import HatchPattern from '../cash-cockpit/charts/HatchPattern';
import WeekDetailTable from '../cash-cockpit/WeekDetailTable';
import { formatMoney } from '../cash-cockpit/charts/IbcsTooltip';

interface Props {
    companyCode: string;
    refreshKey?: number;
}

/** Special sentinel for group-consolidated EUR view (all currencies FX-converted). */
const CONSOLIDATED_MODE = 'ALL';
const CURRENCY_SORT_ORDER = ['EUR', 'CHF', 'USD', 'GBP'];

/**
 * Self-contained 13-week forecast for a single company, shown on the
 * Bank Balance page below the Planned Events editor.
 *
 * Currency toggle lets the country manager switch between group-consolidated
 * EUR and a per-currency native view (filters flows + AR/AP by CurrencyCode,
 * no FX conversion) — critical for FX planning/hedging.
 */
export default function CashOutlook({ companyCode, refreshKey = 0 }: Props) {
    const [data, setData] = useState<ForecastData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // 'ALL' = group-consolidated EUR (FX). ISO code = filter to that native currency.
    const [mode, setMode] = useState<string>(CONSOLIDATED_MODE);
    const [presentCurrencies, setPresentCurrencies] = useState<string[]>([]);

    // Collect currencies actually in use for this company: from planned events AND
    // from the company's bank accounts (for the opening balance side).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [events, accounts] = await Promise.all([
                    plannedEventsApi.list(companyCode).catch(() => []),
                    cashCockpitApi.bankAccounts().catch(() => []),
                ]);
                const fromEvents = events.map(e => e.CurrencyCode).filter(Boolean);
                const fromAccounts = accounts
                    .filter(a => a.companyCode === companyCode)
                    .map(a => a.currencyCode)
                    .filter((c): c is string => !!c);
                const merged = Array.from(new Set([...fromEvents, ...fromAccounts])).sort((a, b) => {
                    const ia = CURRENCY_SORT_ORDER.indexOf(a);
                    const ib = CURRENCY_SORT_ORDER.indexOf(b);
                    if (ia !== -1 && ib !== -1) return ia - ib;
                    if (ia !== -1) return -1;
                    if (ib !== -1) return 1;
                    return a.localeCompare(b);
                });
                if (!cancelled) setPresentCurrencies(merged);
            } catch {
                if (!cancelled) setPresentCurrencies([]);
            }
        })();
        return () => { cancelled = true; };
    }, [companyCode]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await cashCockpitApi.forecast([companyCode], 13, undefined, mode);
            setData(result);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load forecast');
        } finally {
            setLoading(false);
        }
    }, [companyCode, mode]);

    useEffect(() => { void load(); }, [load, refreshKey]);

    const aggregated = useMemo(() => {
        if (!data || data.forecast.length === 0) return null;
        const first = data.forecast[0];
        const last = data.forecast[data.forecast.length - 1];
        const totalInflow = data.forecast.reduce((s, w) => s + w.arInflowEUR + w.plannedInflowEUR, 0);
        const totalOutflow = data.forecast.reduce((s, w) => s + w.apOutflowEUR + w.plannedOutflowEUR, 0);
        const netChange = last.closingCashEUR - first.openingCashEUR;
        const flags = Array.from(new Set(data.forecast.flatMap(w => w.flags ?? [])));
        return { first, last, totalInflow, totalOutflow, netChange, flags, isLimited: first.isLimitedForecast };
    }, [data]);

    if (loading && !data) {
        return <div className="p-6 text-center text-gray-500 text-sm">Loading forecast…</div>;
    }

    if (error) {
        return (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
                <AlertCircle className="w-4 h-4" /> {error}
            </div>
        );
    }

    if (!data || !aggregated) return null;
    const { first, last, totalInflow, totalOutflow, netChange, flags, isLimited } = aggregated;
    const isConsolidated = mode === CONSOLIDATED_MODE;
    const displayCurrency = data.displayCurrency || (isConsolidated ? 'EUR' : mode);
    const isNative = !isConsolidated;

    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <HatchPattern />
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 gap-3">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">Cash Outlook — next 13 weeks</h3>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 uppercase tracking-wide">Currency</span>
                    <div className="inline-flex flex-wrap bg-gray-100 rounded-md p-0.5 gap-0.5" role="tablist">
                        <button
                            key={CONSOLIDATED_MODE}
                            onClick={() => setMode(CONSOLIDATED_MODE)}
                            className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                                mode === CONSOLIDATED_MODE
                                    ? 'bg-white text-blue-700 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                            aria-pressed={mode === CONSOLIDATED_MODE}
                            title="All currencies consolidated to EUR via FX rates"
                        >
                            All (EUR)
                        </button>
                        {presentCurrencies.map(c => (
                            <button
                                key={c}
                                onClick={() => setMode(c)}
                                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${
                                    c === mode
                                        ? 'bg-white text-blue-700 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                                aria-pressed={c === mode}
                                title={`Only ${c}-denominated flows (native, no FX)`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {isLimited && (
                <div className="mx-4 mt-3 flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    <Info className="w-3.5 h-3.5" />
                    Limited forecast — manual company without AR/AP detail
                </div>
            )}

            {isNative && (
                <div className="mx-4 mt-3 flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                    <Info className="w-3.5 h-3.5 mt-0.5" />
                    <span>
                        Showing only {mode}-denominated flows in native {mode} (no FX conversion).
                        AR/AP included only where BC invoices are posted in {mode}.
                    </span>
                </div>
            )}

            {flags.length > 0 && !isLimited && (
                <div className="mx-4 mt-3 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    <Info className="w-3.5 h-3.5 mt-0.5" />
                    <ul className="list-disc ml-4 space-y-0.5">
                        {flags.map(f => <li key={f}>{f}</li>)}
                    </ul>
                </div>
            )}

            {/* KPI tiles */}
            <div className="grid grid-cols-4 gap-4 p-4 border-b border-gray-200">
                <KpiTile label="Opening Cash" value={first.openingCashEUR} currency={displayCurrency} hint={`on ${first.weekStart}`} />
                <KpiTile label={`Expected Inflows (${displayCurrency})`} value={totalInflow} currency={displayCurrency} semantic="positive" />
                <KpiTile label={`Expected Outflows (${displayCurrency})`} value={totalOutflow} currency={displayCurrency} semantic="negative" />
                <KpiTile label="Closing Cash (W13)" value={last.closingCashEUR} currency={displayCurrency} delta={netChange} hint={`on ${last.weekEnd}`} />
            </div>

            <div className="p-4 space-y-4">
                <WaterfallChart data={data.forecast} height={280} displayCurrency={displayCurrency} />
                <div className="text-xs text-gray-500 text-center">
                    Hatched bars represent the forecast (projection based on planned events
                    {isLimited ? ' and last known balance' : ' + open AR/AP + planned events'}).
                    Update your balance entries and planned events above to refine the forecast.
                </div>
                <WeekDetailTable
                    forecast={data.forecast}
                    displayCurrency={displayCurrency}
                    entities={[companyCode]}
                    currencyMode={mode}
                />
            </div>
        </div>
    );
}

function KpiTile({
    label, value, currency, delta, hint, semantic,
}: { label: string; value: number; currency: string; delta?: number; hint?: string; semantic?: 'positive' | 'negative' }) {
    const color = semantic === 'positive' ? 'text-[#2C7BB6]'
        : semantic === 'negative' ? 'text-[#D7191C]' : 'text-gray-900';
    return (
        <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
            <div className={`text-xl font-semibold font-mono tabular-nums ${color}`}>
                {formatMoney(value, currency)}
            </div>
            {delta != null && (
                <div className={`text-xs font-mono ${delta >= 0 ? 'text-[#2C7BB6]' : 'text-[#D7191C]'}`}>
                    {delta >= 0 ? '▲' : '▼'} {formatMoney(Math.abs(delta), currency)} vs opening
                </div>
            )}
            {hint && <div className="text-xs text-gray-400">{hint}</div>}
        </div>
    );
}
